<template>
  <div class="w-full pb-56" v-if="props.opponentInfo && props.chatInfo">
    <div class="flex flex-col items-center">
      <se-avatar
        :id="props.opponentInfo.id"
        :name="props.opponentInfo.name"
        :src="props.opponentInfo.avatar"
        :size="68"
        class="mb-24"
      />
      <template v-if="props.chatInfo && props.chatInfo.id && props.opponentInfo.role_name === 'advisor'">
        <Link class="text-h-l font-bold text-center"
              :href="{ path: '/advisor/:url', params: { url: props.opponentInfo.url } }"
              v-if="props.opponentInfo.role_name === 'advisor'">
          {{ props.opponentInfo.name }}
        </Link>
      </template>
      <h3 v-else-if="props.chatInfo && props.chatInfo.id" class="text-h-l font-bold text-center">
        {{ props.opponentInfo.name }}</h3>
    </div>
    <div class="side-panel-collapsible">
      <se-accordion v-if="isAdvisor" title="Payments">
        <div class="flex justify-between gap-12 py-24">
          <div>
            <p class="text-b-xs text-neutral-50">Earned from Inbox</p>
            <p class="text-h-m font-semibold">${{ Number(totalInboxEarnings).toFixed(2) }}</p>
          </div>
          <div class="h-[47px] w-[0.5px] bg-neutral-30"/>
          <div>
            <p class="text-b-xs text-neutral-50">Earned from Sessions</p>
            <p class="text-h-m font-semibold">${{ Number(totalChatEarnings).toFixed(2) }}</p>
          </div>
        </div>
      
      </se-accordion>
      <se-accordion title="Recent Sessions">
        <div class="" v-if="recentSessions.length">
          <ul>
            <li v-for="chat in recentSessions"
                @click="goToChatHistory(chat.id)"
                class="flex items-center justify-between py-12 border-t-[0.5px] border-t-neutral-30 cursor-pointer">
              <div class="flex items-center gap-16">
                <se-chat-option :chat-action="chat.mode" size="16" class="p-8"/>
                <div>
                  <p class="text-h-s font-bold text-neutral-80">{{ chatTypeContent[chat.mode] }}</p>
                  <p class="text-b-xs text-neutral-60">
                    {{ useDateFormatter().formatDate(chat.created_at, 'MMM d, y') }}</p>
                </div>
              </div>
              <p class="text-h-s font-semibold text-neutral-60">
                {{ formatDurationShort(chat.created_at, chat.ended_at) }}</p>
            </li>
          </ul>
        </div>
        <div class="card-body" v-else>
          No chat sessions yet...
        </div>
      </se-accordion>
      <se-accordion title="Session Notes">
        <div v-if="props.chatInfo && props.chatInfo.id">
          <se-session-notes
            :opponent-id="props.opponentInfo.id"
            :notes="props.opponentInfo.my_notes"
          />
        </div>
      </se-accordion>
    </div>
  </div>
</template>

<script setup lang="ts">
import {storeToRefs} from "pinia";
import {computed, onMounted} from "vue";
import {usersStore} from "@/src/store/users";
import Link from "@/src/components/Link.vue";
import SeAvatar from "@/src/shared/ui-kit/avatar/Avatar.vue";
import SeAccordion from "@/src/shared/ui-kit/accordion/Accordion.vue";
import usePayments from "@/src/composables/usePayments";
import useSessions from "@/src/composables/useSessions";
import useDateFormatter from "@/src/composables/useDateFormatter";
import SeChatOption from "@/src/shared/ui-kit/chat-option/ChatOption.vue";
import SeSessionNotes from "@/src/modules/shared/components/session-notes.vue";
import {getGlobalProperties} from "@/src/shared/helpers/getGlobalProperties";

const props = withDefaults(defineProps<{
  opponentInfo: any,
  chatInfo: any,
}>(), {});

const emits = defineEmits(['editNote', 'deleteNote']);

const users = usersStore()
const {isAdvisor} = storeToRefs(users)
const {setNoteModalState} = users

const {getPaymentsInfo, inboxTransactions, chatTransactions} = usePayments()
const {getRecentSessions, recentSessions} = useSessions()

const {$router} = getGlobalProperties()

onMounted(() => {
  getPaymentsInfo()
  getRecentSessions()
})

const totalInboxEarnings = computed(() => inboxTransactions.value ? inboxTransactions.value.chargings.reduce((accumulator: number, item: any) => accumulator + parseFloat(item.amount), 0) : 0)
const totalChatEarnings = computed(() => chatTransactions.value ? chatTransactions.value.reduce((accumulator: number, item: any) => accumulator + parseFloat(item.user_amount), 0) : 0)

const chatTypeContent = {
  text: "Text Chat",
  audio: "Voice Call",
  video: "Video Call",
}

const formatDurationShort = (start: string, end: string) => {
  const duration = useDateFormatter().calcDuration(start, end)
  
  const hours = duration.hours ? `${duration.hours}h ` : '';
  const minutes = duration.minutes ? `${String(duration.minutes).padStart(2, '0')}min ` : '00min ';
  const seconds = duration.seconds ? `${String(duration.seconds).padStart(2, '0')}s` : '00s';
  
  return `${hours}${minutes}${seconds}`.trim();
}

const goToChatHistory = (id: number) => {
  window.open(`/dashboard/history/${id}`, '_blank');
}

</script>

<script lang="ts">
export default {
  name: 'SeSessionSidePanel',
};
</script>

<style lang="scss">
.side-panel-collapsible {
  @apply mt-24 border-t-[0.5px] border-neutral-30;
  
  .c-accordion {
    @apply border-b-[0.5px] border-neutral-30;
  }
}
</style>